import React, { useEffect, useMemo, useState } from 'react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import Input from '../ui/Input';
import { createYearStructurePeriod, getScheduleEntries } from '../../services/apiService';
import AcademicYearService from '../../services/AcademicYearService';
import { getWeekDateRange } from '../../utils/timeUtils';
import {
    formatPeriodBoundsLabel,
    formatPeriodShortMonthYearRange,
    getDraftPeriodBoundsFromEntries,
    normalizePeriodFromApi,
    validateNewPeriod
} from '../../utils/yearStructurePeriodUtils';

const DEFAULT_PERIOD_COLOR = '#3B82F6';

const formatWeekDate = (date) => {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
    });
};

const MakePeriodFromDraftModal = ({
    isOpen,
    onClose,
    draft = null,
    sourcePeriod = null,
    livePeriodName = '',
    onSuccess,
    setError
}) => {
    const draftName = draft?.draft_name || '';
    const [name, setName] = useState('');
    const [color, setColor] = useState(DEFAULT_PERIOD_COLOR);
    const [bounds, setBounds] = useState(null);
    const [isLoadingBounds, setIsLoadingBounds] = useState(false);
    const [boundsError, setBoundsError] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (!isOpen) return;

        setName(draftName || '');
        setColor(DEFAULT_PERIOD_COLOR);
        setBounds(null);
        setBoundsError('');
        setIsSaving(false);

        let cancelled = false;

        const loadBounds = async () => {
            if (!draftName) {
                setBoundsError('This draft cannot be used to create a period.');
                return;
            }

            setIsLoadingBounds(true);
            try {
                if (!sourcePeriod) {
                    setBounds(null);
                    setBoundsError('This draft is not attached to a period, so its week years cannot be resolved.');
                    return;
                }

                const rawEntries = await getScheduleEntries({ draft_name: draftName });
                if (cancelled) return;

                const entries = Array.isArray(rawEntries) ? rawEntries : [];
                const nextBounds = getDraftPeriodBoundsFromEntries(entries, sourcePeriod);

                if (!nextBounds) {
                    setBounds(null);
                    setBoundsError('This draft has no scheduled weeks. Add schedule entries before creating a period.');
                    return;
                }

                setBounds(nextBounds);
                setBoundsError('');
            } catch (err) {
                if (cancelled) return;
                console.error('Error loading draft weeks for new period:', err);
                setBounds(null);
                setBoundsError(err?.message || "Failed to read this draft's schedule weeks.");
            } finally {
                if (!cancelled) setIsLoadingBounds(false);
            }
        };

        loadBounds();
        return () => {
            cancelled = true;
        };
    }, [isOpen, draftName, sourcePeriod]);

    const previewPeriod = useMemo(() => {
        if (!bounds) return null;
        return {
            name: name.trim() || 'Period',
            startWeek: bounds.startWeek,
            start_week: bounds.startWeek,
            endWeek: bounds.endWeek,
            end_week: bounds.endWeek,
            startWeekYear: bounds.startWeekYear,
            start_week_year: bounds.startWeekYear,
            endWeekYear: bounds.endWeekYear,
            end_week_year: bounds.endWeekYear,
            year: bounds.startWeekYear,
            color
        };
    }, [bounds, name, color]);

    const dateRangeLabel = useMemo(() => {
        if (!bounds) return '';
        const startRange = getWeekDateRange(bounds.startWeekYear, bounds.startWeek);
        const endRange = getWeekDateRange(bounds.endWeekYear, bounds.endWeek);
        const startLabel = formatWeekDate(startRange?.startDate);
        const endLabel = formatWeekDate(endRange?.endDate);
        if (!startLabel || !endLabel) return '';
        return `${startLabel} – ${endLabel}`;
    }, [bounds]);

    const handleClose = () => {
        if (isSaving) return;
        onClose();
    };

    const handleCreate = async () => {
        if (!draftName || !bounds) return;

        const validation = validateNewPeriod({
            name,
            startKey: `${bounds.startWeekYear}|${bounds.startWeek}`,
            endKey: `${bounds.endWeekYear}|${bounds.endWeek}`,
            color
        });
        if (!validation.valid) {
            if (setError) setError(validation.error);
            setBoundsError(validation.error);
            return;
        }

        setIsSaving(true);
        if (setError) setError(null);
        try {
            const data = await createYearStructurePeriod({
                ...validation.payload,
                associateDraftName: draftName
            });
            AcademicYearService.getInstance().clearCache();

            const periods = (data?.yearStructure?.periods || []).map(normalizePeriodFromApi);
            const savedPeriod = data?.period
                ? normalizePeriodFromApi(data.period)
                : periods.find((period) => (
                    period.name === validation.payload.name
                    && (period.startWeek ?? period.start_week) === validation.payload.startWeek
                    && (period.endWeek ?? period.end_week) === validation.payload.endWeek
                )) || periods[periods.length - 1] || null;

            if (onSuccess) {
                await onSuccess({
                    period: savedPeriod,
                    yearStructure: data?.yearStructure
                        ? { ...data.yearStructure, periods }
                        : null
                });
            }
            onClose();
        } catch (err) {
            console.error('Error creating period from draft:', err);
            const message = err?.message || 'Failed to create period from this draft.';
            if (setError) setError(message);
            setBoundsError(message);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title="Make new period"
            containerClassName="max-w-lg max-h-[80vh]"
        >
            <div className="space-y-4">
                <p className="text-sm text-gray-600">
                    Create a period from “{draftName || 'this draft'}” and attach the draft to it.
                    It will not become live until you set it live separately.
                </p>

                {livePeriodName ? (
                    <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                        This draft is currently live for {livePeriodName}. Creating a new period will remove it from live.
                    </div>
                ) : null}

                <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-3">
                    <p className="text-[11px] font-medium uppercase tracking-[0.08em] text-gray-400">
                        Period time range
                    </p>
                    {isLoadingBounds ? (
                        <p className="mt-1 text-sm text-gray-500">Reading schedule weeks…</p>
                    ) : previewPeriod ? (
                        <>
                            {dateRangeLabel ? (
                                <p className="mt-1 text-base font-semibold text-gray-900">{dateRangeLabel}</p>
                            ) : null}
                            <p className="mt-0.5 text-sm text-gray-600">
                                {formatPeriodShortMonthYearRange(previewPeriod)}
                                {formatPeriodBoundsLabel(previewPeriod) ? (
                                    <span className="text-gray-400"> · {formatPeriodBoundsLabel(previewPeriod)}</span>
                                ) : null}
                            </p>
                            <p className="mt-1 text-xs text-gray-500">
                                Clipped to the earliest and latest weeks in this draft's schedule entries.
                            </p>
                        </>
                    ) : (
                        <p className="mt-1 text-sm text-gray-500">No week range available.</p>
                    )}
                </div>

                {boundsError ? (
                    <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                        {boundsError}
                    </div>
                ) : null}

                <Input
                    label="Period name"
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                    placeholder="e.g. Fall 2026"
                    required
                    disabled={isSaving || isLoadingBounds}
                    autoFocus
                />

                <div>
                    <label htmlFor="make-period-color" className="block text-sm font-medium text-gray-700 mb-1">
                        Color
                    </label>
                    <div className="flex items-center gap-3">
                        <input
                            id="make-period-color"
                            type="color"
                            value={color}
                            onChange={(event) => setColor(event.target.value)}
                            disabled={isSaving || isLoadingBounds}
                            className="h-10 w-12 cursor-pointer rounded-md border border-gray-300 bg-white p-1 disabled:cursor-not-allowed disabled:opacity-60"
                            aria-label="Period color"
                        />
                        <span className="text-sm text-gray-500">{color}</span>
                    </div>
                </div>
            </div>

            <div className="mt-6 flex justify-end gap-2">
                <Button variant="secondary" onClick={handleClose} disabled={isSaving}>
                    Cancel
                </Button>
                <Button
                    variant="primary"
                    onClick={handleCreate}
                    disabled={isSaving || isLoadingBounds || !bounds || !name.trim()}
                >
                    {isSaving ? 'Creating…' : 'Create period'}
                </Button>
            </div>
        </Modal>
    );
};

export default MakePeriodFromDraftModal;
